import TenantTag from '@components/TenantTag/TenantTag';
import noop from '@lib/utils/noop';
import { inboxLocators } from '@locators/inbox';
import {
  Button,
  Card,
  CardSection,
  Form,
  Headline,
  Stack,
  Tooltip,
} from '@servicetitan/design-system';
import { ChangeEvent } from 'react';
import styles from './CustomerSearchBox.module.css';
import { useCustomerSearchContext } from '@components/CustomerSearch/context';

export type SearchParams = {
  name?: string;
  street?: string;
  unit?: string;
  zip?: string;
  phone?: string;
};

type Props = {
  tenantId?: string;
  disabled?: boolean;
  loading?: boolean;
  onSearch?: (params: SearchParams) => void;
  onChanged?: (params: SearchParams) => void;
  onCleared?: () => void;
};

const CustomerSearchBox = ({
  tenantId,
  disabled = false,
  loading = false,
  onSearch = noop,
  onChanged = noop,
  onCleared = noop,
}: Props) => {
  const {
    searchParams: params,
    setSearchParams: setParams,
    onAddCustomer,
  } = useCustomerSearchContext();

  const handleSearch = () => {
    onSearch(params);
  };

  const handleClear = () => {
    onChanged({});
    setParams({
      name: '',
      phone: '',
      street: '',
      unit: '',
      zip: '',
    });
    onCleared();
  };

  const updateSearchParam = (key: keyof SearchParams, value: string) => {
    const newParams = { ...params, [key]: value };
    setParams(newParams);
    onChanged(newParams);
  };

  const emptyParams =
    !params.name &&
    !params.phone &&
    !params.street &&
    !params.unit &&
    !params.zip;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !emptyParams) {
      handleSearch();
    }
  };

  return (
    <Card>
      <CardSection className='p-3 p-r-2'>
        <Stack direction='column' spacing={3}>
          <Stack alignItems='center' justifyContent='space-between'>
            <Stack spacing={2}>
              <Headline
                className='m-0'
                id={inboxLocators.customerSearch.searchTitle}
                size='small'
              >
                Search for Customer
              </Headline>
              <TenantTag active tenantNameOrId={tenantId ?? 'All Tenants'} />
            </Stack>
            {onAddCustomer && (
              <Button
                id={inboxLocators.customerSearch.newCustomerBtn}
                fill='subtle'
                size='small'
                primary
                iconName='add_circle'
                disabled={disabled}
                onClick={onAddCustomer}
              >
                New Customer
              </Button>
            )}
          </Stack>
          <div className='gap-2 d-f flex-wrap'>
            <Form.Input
              className={`m-b-0 ${styles.customerSearchBoxNameInput}`}
              label='Name'
              id={inboxLocators.customerSearch.searchForm.name}
              placeholder='Search Name'
              name='sp2'
              size='small'
              value={params.name ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateSearchParam('name', e.target.value)
              }
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
            <Form.Input
              className={`m-b-0 ${styles.customerSearchBoxStreetInput}`}
              label='Street'
              id={inboxLocators.customerSearch.searchForm.street}
              name='sp3'
              placeholder='Search Address'
              size='small'
              value={params.street ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateSearchParam('street', e.target.value)
              }
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
            <Form.Input
              className={`m-b-0 ${styles.customerSearchBoxUnitInput}`}
              label='Unit'
              name='sp4'
              placeholder='Unit'
              size='small'
              value={params.unit ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateSearchParam('unit', e.target.value)
              }
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
            <Form.Input
              className={`m-b-0 ${styles.customerSearchBoxZipInput}`}
              label='ZIP Code'
              id={inboxLocators.customerSearch.searchForm.zip}
              name='sp5'
              placeholder='Zip'
              size='small'
              value={params.zip ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateSearchParam('zip', e.target.value)
              }
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
            <Form.Input
              className={`m-b-0 ${styles.customerSearchBoxPhoneInput}`}
              label='Phone'
              id={inboxLocators.customerSearch.searchForm.phone}
              name='sp6'
              placeholder='(314) 333-3333'
              size='small'
              value={params.phone ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateSearchParam('phone', e.target.value)
              }
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
            <Button
              primary
              outline
              onClick={handleSearch}
              id={inboxLocators.customerSearch.searchBtn}
              disabled={disabled || loading || emptyParams}
              loading={loading}
              size='small'
              className='m-t-3'
            >
              Search
            </Button>
            {!emptyParams && (
              <Tooltip text='Clear Search' portal direction='bottom'>
                <Button
                  className='m-t-3'
                  aria-label='Clear Search'
                  fill='subtle'
                  size='small'
                  iconName='close'
                  disabled={disabled || loading || emptyParams}
                  onClick={handleClear}
                />
              </Tooltip>
            )}
          </div>
        </Stack>
      </CardSection>
    </Card>
  );
};

export default CustomerSearchBox;
